
    import { computed, defineComponent, onBeforeMount, toRef, watch } from 'vue'
    import { useRouter } from 'vue-router/composables'

    import NotFoundError from '@/common/errors/not-found-error'

    import { useHeadful } from '@/common/composables/libs/headful'
    import { useI18n } from 'vue-i18n-composable'
    import PostPreviewList from '@/app/components/common/posts/PostPreviewList.vue'

    import { useBlogStore } from '@/app/stores/blog'
    import { useBreadcrumbsStore } from '@/app/stores/breadcrumbs'
    import { useCategoriesStore } from '@/app/stores/categories'

    export default defineComponent({
        components: {
            PostPreviewList
        },
        props: {
            slug: {
                type: String,
                required: true
            },
            page: {
                type: Number,
                default: 1
            }
        },
        setup (props) {
            const headful = useHeadful()
            const i18n = useI18n()
            const router = useRouter()
            const blogStore = useBlogStore()
            const categoriesStore = useCategoriesStore()

            const data = computed(() => blogStore.byCategory(props.slug))
            const entries = computed(() => data.value.data)
            const paginationMeta = computed(() => data.value.meta)

            const paginate = async (page: number): Promise<void> => {
                await router.push({
                    name: 'blog.by-category',
                    params: {
                        slug: props.slug,
                        page: page.toString()
                    }
                })

                await blogStore.loadWithCategory({
                    categorySlug: props.slug,
                    page: page
                })
            }

            const loadPage = async (page: number | undefined = undefined): Promise<void> => {
                if (props.slug === '') {
                    throw new NotFoundError()
                }

                // Ensure that categories store is loaded, then load category data
                // Loading store here is needed because if we enter the url directly, there can be race-condition
                // between categories module and this and category becomes undefined.
                // Therefore, we force categories store loading here
                await categoriesStore.load()
                const category = categoriesStore.bySlug(props.slug)

                if (!category) {
                    throw new NotFoundError()
                }

                // We loaded users store, so now we are safe that user won't be undefined, and we can safely
                // set breadcrumbs and metadata
                useBreadcrumbsStore().set([
                    { text: i18n.t('nav.blog.blog').toString(), to: { name: 'blog' } },
                    { text: category.category }
                ])

                headful({
                    title: `${category.category} | ${i18n.t('meta.title')}`
                })

                await blogStore.loadWithCategory({
                    categorySlug: props.slug,
                    page: page ?? props.page
                })
            }

            onBeforeMount(async (): Promise<void> => {
                await loadPage()
            })

            watch(toRef(props, 'slug'), async (): Promise<void> => {
                await Promise.all([
                    router.push({
                        name: 'blog.by-category',
                        params: {
                            slug: props.slug,
                            page: '1'
                        }
                    }),
                    loadPage(1)
                ])
            })

            return {
                entries,
                paginationMeta,
                paginate
            }
        }
    })
